import { useState } from 'react';
import { useWaitingCountdown } from '../../utils/schedule';
import ScheduleBar from '../common/ScheduleBar';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Switch,
  Button,
  Chip,
  Box,
  IconButton,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import api from '../../services/api';
import ActionsList from './ActionsList';
import { workingPulseSx } from '../../theme';
import type { AutoLikerProgress } from '../../hooks/useAutoLikerWebSocket';

interface ConfigCardProps {
  config: {
    id: number;
    name: string;
    keywords: string[];
    maxLikesPerDay: number;
    todayLikes: number;
    totalLikes: number;
    isActive: boolean;
    minPostDate?: string | null;
    lastRunAt?: string | null;
    account: {
      firstName: string;
      lastName: string;
    };
  };
  onUpdate: () => void;
  onDelete: (id: number) => void;
  progress?: AutoLikerProgress | null;
}


// Convert "minPostDate" (ISO string from API) → number of days back from now,
// rounded to the nearest preset. Returns 0 if the field is absent ("без ограничения").
function deriveAgeDays(minPostDate?: string | null): 0 | 7 | 14 | 30 | 90 {
  if (!minPostDate) return 0;
  const diffMs = Date.now() - new Date(minPostDate).getTime();
  const days = Math.round(diffMs / (24 * 60 * 60 * 1000));
  const presets = [7, 14, 30, 90];
  // Snap to the closest preset
  let closest: 0 | 7 | 14 | 30 | 90 = 7;
  let bestDiff = Infinity;
  for (const p of presets) {
    const d = Math.abs(days - p);
    if (d < bestDiff) {
      bestDiff = d;
      closest = p as 7 | 14 | 30 | 90;
    }
  }
  return closest;
}

const getSkipReasonText = (reason: string): string => {
  switch (reason) {
    case 'already_liked': return 'Уже лайкнуто';
    case 'daily_limit': return 'Лимит достигнут';
    case 'error': return 'Ошибка';
    default: return reason;
  }
};

const getPhaseIcon = (phase: AutoLikerProgress['phase']) => {
  switch (phase) {
    case 'searching': return <SearchIcon fontSize="small" />;
    case 'processing': return <SettingsIcon fontSize="small" />;
    case 'waiting': return <HourglassIcon fontSize="small" />;
    case 'completed': return <CheckCircleIcon fontSize="small" />;
    case 'error': return <ErrorIcon fontSize="small" />;
  }
};

const getPhaseColor = (phase: AutoLikerProgress['phase']): string => {
  switch (phase) {
    case 'searching': return 'info.main';
    case 'processing': return 'primary.main';
    case 'waiting': return 'warning.main';
    case 'completed': return 'success.main';
    case 'error': return 'error.main';
  }
};

export default function ConfigCard({ config, onUpdate, onDelete, progress }: ConfigCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: config.name,
    keywords: config.keywords.join(', '),
    maxLikesPerDay: config.maxLikesPerDay,
    postAgeDays: deriveAgeDays(config.minPostDate),
  });
  const openEdit = () => {
    setEditForm({
      name: config.name,
      keywords: config.keywords.join(', '),
      maxLikesPerDay: config.maxLikesPerDay,
      postAgeDays: deriveAgeDays(config.minPostDate),
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    const cleanKeywords = editForm.keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);
    if (!editForm.name.trim() || cleanKeywords.length === 0) {
      toast.error('Имя и ключевые слова обязательны');
      return;
    }
    setEditSaving(true);
    try {
      const payload: any = {
        name: editForm.name.trim(),
        keywords: cleanKeywords,
        maxLikesPerDay: editForm.maxLikesPerDay,
      };
      // null clears the field on the backend; ISO string sets it.
      if (editForm.postAgeDays > 0) {
        payload.minPostDate = new Date(
          Date.now() - editForm.postAgeDays * 24 * 60 * 60 * 1000
        ).toISOString();
      } else {
        payload.minPostDate = null;
      }
      await api.patch(`/auto-liker/${config.id}`, payload);
      toast.success('Конфигурация обновлена');
      setEditOpen(false);
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setEditSaving(false);
    }
  };

  // Delete confirm dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await api.post(`/auto-liker/${config.id}/toggle`);
      toast.success(config.isActive ? 'Правило выключено' : 'Правило включено');
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось переключить правило');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/auto-liker/${config.id}`);
      toast.success('Конфигурация удалена');
      onDelete(config.id);
      setDeleteOpen(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось удалить');
    } finally {
      setDeleting(false);
    }
  };

  // Run now — kick the per-config job without waiting for the 2h scheduler
  const [runningNow, setRunningNow] = useState(false);
  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const res = await api.post(`/auto-liker/${config.id}/run`);
      const { queued } = res.data.data || {};
      if (queued) {
        toast.success('Запущено — следите за прогрессом в карточке');
      } else {
        // Backend returned 200 with reason ("limit reached" / "token invalid")
        toast(res.data.message || 'Не запущено', { icon: '⚠️' });
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось запустить');
    } finally {
      setRunningNow(false);
    }
  };

  // Local 1Hz countdown driven by waitUntilMs/waitMs from the server.
  const { secLeft: waitSecLeft, pct: waitProgressPct, totalSec: waitTotalSec } =
    useWaitingCountdown(progress);

  // Is the worker currently doing something on this config?
  const isWorking =
    !!progress &&
    progress.phase !== 'completed' &&
    progress.phase !== 'error';

  const dailyProgress = (config.todayLikes / config.maxLikesPerDay) * 100;

  return (
    <Card
      sx={{
        mb: 2,
        borderColor: isWorking ? 'primary.main' : undefined,
        transition: 'border-color 0.3s, box-shadow 0.3s',
        ...(isWorking ? workingPulseSx : {}),
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {config.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Account: {config.account.firstName} {config.account.lastName}
            </Typography>
            <Box sx={{ mt: 1 }}>
              {config.keywords.map((kw, i) => (
                <Chip key={i} label={kw} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
              ))}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary">
                Today: {config.todayLikes} / {config.maxLikesPerDay}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total: {config.totalLikes}
              </Typography>
              <Box
                sx={{
                  width: 100,
                  height: 6,
                  bgcolor: 'grey.300',
                  borderRadius: 1,
                  mt: 0.5,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${Math.min(dailyProgress, 100)}%`,
                    height: '100%',
                    bgcolor: dailyProgress >= 100 ? 'error.main' : 'primary.main',
                    transition: 'width 0.3s',
                  }}
                />
              </Box>
            </Box>
            <Switch
              checked={config.isActive}
              onChange={handleToggle}
              disabled={isToggling}
              color="primary"
            />
          </Box>
        </Box>

        <ScheduleBar
          isActive={config.isActive}
          lastRunAt={config.lastRunAt}
          isWorking={isWorking}
          runningNow={runningNow}
          onRunNow={handleRunNow}
        />

        {/* Status Indicator */}
        {progress && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: 1,
              borderColor: getPhaseColor(progress.phase),
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={progress.details?.postsFound ? 1 : 0}>
              <Box sx={{ color: getPhaseColor(progress.phase) }}>
                {getPhaseIcon(progress.phase)}
              </Box>
              <Typography variant="body2" sx={{ color: getPhaseColor(progress.phase) }}>
                {waitSecLeft !== null
                  ? `Ожидание следующего действия: ${waitSecLeft} сек…`
                  : progress.message}
              </Typography>
            </Box>

            {/* Countdown progress bar (waiting phase) */}
            {waitSecLeft !== null && waitTotalSec > 0 && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={waitProgressPct}
                  color="warning"
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {waitSecLeft} из {waitTotalSec} сек
                </Typography>
              </Box>
            )}

            {/* Progress bar for processing phase */}
            {progress.phase === 'processing' && progress.details?.postsFound && progress.details.postsFound > 0 && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={((progress.details.postsProcessed || 0) / progress.details.postsFound) * 100}
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {progress.details.postsProcessed || 0} / {progress.details.postsFound}
                </Typography>
              </Box>
            )}

            {/* Skipped reasons */}
            {progress.details?.skippedReasons && progress.details.skippedReasons.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {progress.details.skippedReasons.map((r, i) => (
                  <Chip
                    key={i}
                    label={`${getSkipReasonText(r.reason)}: ${r.count}`}
                    size="small"
                    variant="outlined"
                    color={r.reason === 'error' ? 'error' : 'default'}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
          <Button
            size="small"
            startIcon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.3s',
                }}
              />
            }
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Скрыть' : 'Показать'} историю
          </Button>
        </Box>
        <Box>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={openEdit}
            sx={{ mr: 1 }}
          >
            Изменить
          </Button>
          <IconButton color="error" size="small" onClick={() => setDeleteOpen(true)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardActions>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          <ActionsList configId={config.id} />
        </CardContent>
      </Collapse>

      {/* ===== Edit dialog ===== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Изменить конфигурацию</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Аккаунт: {config.account.firstName} {config.account.lastName} (менять нельзя — токен
              привязан к конфигурации; для смены аккаунта создайте новую)
            </Typography>
            <TextField
              fullWidth
              label="Название"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Ключевые слова (через запятую)"
              value={editForm.keywords}
              onChange={(e) => setEditForm({ ...editForm, keywords: e.target.value })}
              helperText='Логика "И": пост должен содержать ВСЕ слова. Пример: "мы, открылись"'
            />
            <TextField
              fullWidth
              type="number"
              label="Макс. лайков в день"
              value={editForm.maxLikesPerDay}
              onChange={(e) =>
                setEditForm({ ...editForm, maxLikesPerDay: parseInt(e.target.value) || 1 })
              }
              inputProps={{ min: 1, max: 200 }}
            />
            <TextField
              select
              fullWidth
              label="Возраст постов"
              value={editForm.postAgeDays}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  postAgeDays: parseInt(e.target.value) as 0 | 7 | 14 | 30 | 90,
                })
              }
              SelectProps={{ native: true }}
              helperText="Лайкать только посты не старше выбранного периода"
            >
              <option value={7}>За последние 7 дней</option>
              <option value={14}>За последние 14 дней</option>
              <option value={30}>За последние 30 дней</option>
              <option value={90}>За последние 90 дней</option>
              <option value={0}>Без ограничения</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={submitEdit} disabled={editSaving}>
            {editSaving ? 'Сохраняю…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Delete confirm ===== */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Удалить конфигурацию?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography>
              Будет удалена конфигурация <b>«{config.name}»</b> и вся история её действий.
            </Typography>
            <Alert severity="warning">
              VK-аккаунт и накопленные счётчики аккаунта останутся — удаляется только эта конкретная
              автолайкер-конфигурация.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Удаляю…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
